// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import { lengthInput, parseLength, type EditField, type FieldKey, type PropertiesView } from '../../../bygning/geometriEdit';
import type { RoomZoneView } from '../../../bygning/zones';
import type { CeilingPlane } from '../../../api/types';
import type { CeilingView, PlaneSpec } from '../../../bygning/ceilingEdit';
import { Button } from '../../Button';
import { CadIcon } from './cadIcons';
import styles from './PropertiesPanel.module.css';

export interface PropertiesPanelProps {
  view: PropertiesView;
  /** More than one selected: the panel shows the primary, and says so. */
  more: number;
  onField: (key: FieldKey, value: number | string) => void;
  onReset: (key: FieldKey) => void;
  onRevert: () => void;
  onDelete: () => void;
  onSelect: (guid: string) => void;
  onZoom: (() => void) | null;
  /** A room's Zone section (S6); absent for other elements or before zones are read. */
  zone?: RoomZoneView | null;
  zoneActions?: ZoneActions;
  /** A room's Loft section (S7): its ceiling planes. */
  ceiling?: CeilingView | null;
  ceilingActions?: CeilingActions;
  /** A slab row's holes (S7). */
  holes?: { count: number; onRemove(index: number): void; onDraw(): void } | null;
  /** Shown under the chips (a stair's side profile and its advice, S8). */
  extra?: ReactNode;
}

export interface CeilingFitPreview {
  summary: string;
  warning: string | null;
  planes: CeilingPlane[] | null;
  apply: (() => void) | null;
}

export interface CeilingActions {
  onPlane(index: number, patch: Partial<PlaneSpec>): void;
  /** A level plane, or the gable's other half. */
  onAddPlane(): void;
  onRemovePlane(index: number): void;
  onFlat(): void;
  /** Tilpas til punktsky: the fit's summary, a caution when it looks wrong, and its apply (null when there is none). */
  onFit(): Promise<CeilingFitPreview>;
  /** The fit's planes drawn in Snit while the preview is up (null: gone). */
  onPreview(planes: CeilingPlane[] | null): void;
  /** Snit across the room. */
  onSection(): void;
}

/** A number typed in a plane's row (degrees or metres): committed on Enter or blur. */
function PlaneNumber({ label, unit, value, digits, canEdit, onCommit }: { label: string; unit: string; value: number; digits: number; canEdit: boolean; onCommit: (v: number) => void }) {
  const shown = value.toFixed(digits).replace('.', ',');
  const [text, setText] = useState(shown);
  useEffect(() => setText(shown), [shown]);
  const commit = () => {
    if (text === shown) return;
    const v = Number(text.trim().replace(',', '.'));
    if (Number.isFinite(v)) onCommit(v);
    else setText(shown);
  };
  return (
    <span className={styles.planeField}>
      <span className={styles.inputWrap}>
        <input
          className={`${styles.input} ${styles.number}`}
          value={text}
          inputMode="decimal"
          aria-label={label}
          title={label}
          readOnly={!canEdit}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setText(shown);
            }
          }}
        />
        <span className={styles.unit}>{unit}</span>
      </span>
    </span>
  );
}

/**
 * Egenskaber's Loft section (S7): the room's ceiling, flat or as planes —
 * each its height over the floor at the room's middle, its slope and the
 * direction it rises toward, typed; a gable's other half; flat again; and
 * Tilpas til punktsky (a fit to the scan over the room, previewed first).
 */
function CeilingSection({ view, canEdit, actions }: { view: CeilingView; canEdit: boolean; actions: CeilingActions }) {
  const [fit, setFitState] = useState<CeilingFitPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const { onPreview } = actions;
  const setFit = (f: CeilingFitPreview | null) => {
    setFitState(f);
    onPreview(f?.planes ?? null);
  };
  useEffect(() => {
    setFitState(null);
    onPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.guid]);
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Loft</h3>
        <button type="button" className={styles.link} onClick={actions.onSection} title="Snit gennem rummet (SN)">
          Vis i snit
        </button>
      </div>
      <p className={styles.ceilingText}>{view.text}</p>
      {view.planes.map((p, i) => (
        <div key={i} className={styles.plane}>
          <span className={styles.planeLabel}>Plan {i + 1}</span>
          <PlaneNumber label={`Plan ${i + 1}: højde over gulv midt i rummet`} unit="m" value={p.height} digits={2} canEdit={canEdit} onCommit={(v) => actions.onPlane(i, { height: v })} />
          <PlaneNumber label={`Plan ${i + 1}: hældning`} unit="°" value={p.deg} digits={1} canEdit={canEdit} onCommit={(v) => actions.onPlane(i, { deg: v })} />
          <PlaneNumber label={`Plan ${i + 1}: stiger mod (grader fra x-aksen)`} unit="°" value={p.dir} digits={0} canEdit={canEdit} onCommit={(v) => actions.onPlane(i, { dir: v })} />
          {canEdit && (
            <button type="button" className={styles.reset} aria-label={`Fjern plan ${i + 1}`} title="Fjern planet" onClick={() => actions.onRemovePlane(i)}>
              ×
            </button>
          )}
        </div>
      ))}
      {view.planes.length > 0 && <p className={styles.planeHint}>Højde over gulv · hældning · stiger mod</p>}
      {canEdit && (
        <div className={styles.ceilingActions}>
          <Button
            size="sm"
            variant="filled"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void actions.onFit().then((r) => {
                setBusy(false);
                setFit(r);
              });
            }}
            title="Find loftets plan i punktskyen over rummet"
          >
            {busy ? 'Tilpasser…' : 'Tilpas til punktsky'}
          </Button>
          <button type="button" className={styles.link} onClick={actions.onAddPlane}>
            {view.planes.length === 1 ? '+ Gavl' : '+ Plan'}
          </button>
          {!view.flat && (
            <button type="button" className={styles.link} onClick={actions.onFlat}>
              Gør fladt
            </button>
          )}
        </div>
      )}
      {fit && (
        <div className={fit.warning ? `${styles.fit} ${styles.fitWarn}` : styles.fit} role="status">
          <p>{fit.summary}</p>
          {fit.warning && <p className={styles.fitWarning}>{fit.warning}</p>}
          <div className={styles.ceilingActions}>
            {fit.apply && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  fit.apply?.();
                  setFit(null);
                }}
              >
                Anvend
              </Button>
            )}
            <Button size="sm" variant="filled" onClick={() => setFit(null)}>
              {fit.apply ? 'Annullér' : 'Luk'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export interface ZoneActions {
  /** Move the room to zone `guid` (null: out of its zone; 'new': a new zone of it). */
  onMove(target: string | null | 'new'): void;
  onField(zone: string, field: 'name' | 'use', value: string): void;
  /** The Zone tool on this zone. */
  onEdit(zone: string): void;
  onDelete(zone: string): void;
}

/** A zone's name or use: committed on Enter or blur, Esc puts it back. */
function ZoneText({ label, value, canEdit, placeholder, onCommit }: { label: string; value: string; canEdit: boolean; placeholder?: string; onCommit: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <span className={styles.inputWrap}>
        <input
          className={styles.input}
          value={text}
          aria-label={`Zone: ${label}`}
          readOnly={!canEdit}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => text !== value && onCommit(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (text !== value) onCommit(text);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setText(value);
            }
          }}
        />
      </span>
      <span />
    </div>
  );
}

/** Egenskaber's Zone section (mock 05): the zone a room is in, its name, use, rooms and totals. */
function ZoneSection({ view, canEdit, actions, onSelect }: { view: RoomZoneView; canEdit: boolean; actions: ZoneActions; onSelect: (guid: string) => void }) {
  const z = view.zone;
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>
          {z && <i className={styles.swatch} style={{ background: `var(${z.swatch})` }} aria-hidden="true" />}
          {z ? `Zone · ${z.name || 'uden navn'}` : 'Zone'}
        </h3>
        {z && canEdit && (
          <button type="button" className={styles.link} onClick={() => actions.onEdit(z.guid)} title="Zone-værktøjet på denne zone: klik rum for at tage dem med eller ud">
            Redigér
          </button>
        )}
      </div>
      <div className={styles.field}>
        <span className={styles.label}>Zone</span>
        <span className={styles.selectWrap}>
          <select
            className={styles.input}
            aria-label="Zone"
            disabled={!canEdit}
            value={z?.guid ?? ''}
            onChange={(e) => actions.onMove(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">Ingen</option>
            {view.options.map((o) => (
              <option key={o.guid} value={o.guid}>
                {o.name}
              </option>
            ))}
            <option value="new">Ny zone af rummet…</option>
          </select>
        </span>
        <span />
      </div>
      {view.others.length > 0 && <p className={styles.more}>Også i {view.others.join(', ')}</p>}
      {z && (
        <>
          <ZoneText label="Navn" value={z.name} canEdit={canEdit} onCommit={(v) => actions.onField(z.guid, 'name', v)} />
          <ZoneText label="Anvendelse" value={z.use} canEdit={canEdit} placeholder="fx bolig" onCommit={(v) => actions.onField(z.guid, 'use', v)} />
          <div className={styles.field}>
            <span className={styles.label}>Rum</span>
            <span className={styles.readonly}>
              {z.rooms.map((r, i) => (
                <span key={r.guid}>
                  {i > 0 && ', '}
                  <button type="button" className={styles.link} onClick={() => onSelect(r.guid)}>
                    {r.label}
                  </button>
                </span>
              ))}
            </span>
            <span />
          </div>
          <div className={styles.field}>
            <span className={styles.label}>Areal i alt</span>
            <span className={styles.readonly}>{z.area}</span>
            <span />
          </div>
          <div className={styles.field}>
            <span className={styles.label}>Volumen i alt</span>
            <span className={styles.readonly}>{z.volume}</span>
            <span />
          </div>
          {canEdit && (
            <p className={styles.related}>
              <button type="button" className={styles.link} onClick={() => actions.onDelete(z.guid)}>
                Slet zone
              </button>
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** A length or text field: typed, committed on Enter or blur, Esc puts the value back. */
function TextValue({ field, canEdit, onCommit }: { field: EditField; canEdit: boolean; onCommit: (value: number | string) => void }) {
  const shown = field.type === 'length' ? (typeof field.value === 'number' ? lengthInput(field.value) : '') : String(field.value ?? '');
  const [text, setText] = useState(shown);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    setText(shown);
    setBad(false);
  }, [shown]);
  const commit = () => {
    if (text === shown) return;
    if (field.type === 'length') {
      const m = parseLength(text);
      if (m === null) {
        setBad(true);
        return;
      }
      onCommit(m);
    } else onCommit(text);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setText(shown);
      setBad(false);
    }
  };
  return (
    <span className={styles.inputWrap}>
      <input
        className={`${styles.input}${field.type === 'length' ? ` ${styles.number}` : ''}${field.edited ? ` ${styles.edited}` : ''}${bad ? ` ${styles.bad}` : ''}`}
        value={text}
        inputMode={field.type === 'length' ? 'decimal' : 'text'}
        aria-label={field.label}
        aria-invalid={bad || undefined}
        readOnly={!canEdit || !field.editable}
        placeholder={field.type === 'length' ? 'ukendt' : field.placeholder}
        onChange={(e) => {
          setText(e.target.value);
          setBad(false);
        }}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />
      {field.type === 'length' && <span className={styles.unit}>m</span>}
    </span>
  );
}

function Field({ field, canEdit, onField, onReset }: { field: EditField; canEdit: boolean; onField: PropertiesPanelProps['onField']; onReset: PropertiesPanelProps['onReset'] }) {
  const id = `prop-${field.key}`;
  let value;
  if (!field.editable || (!canEdit && field.type !== 'choice')) {
    value = <span className={`${styles.readonly}${field.edited ? ` ${styles.edited}` : ''}`}>{field.display}</span>;
  } else if (field.type === 'choice') {
    value = (
      <span className={`${styles.choice}${field.edited ? ` ${styles.edited}` : ''}`} role="radiogroup" aria-labelledby={id}>
        {field.options!.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={o.value === field.value}
            disabled={!canEdit}
            className={o.value === field.value ? `${styles.option} ${styles.optionOn}` : styles.option}
            onClick={() => onField(field.key, o.value)}
          >
            {o.label}
          </button>
        ))}
      </span>
    );
  } else {
    value = <TextValue field={field} canEdit={canEdit} onCommit={(v) => onField(field.key, v)} />;
  }
  return (
    <div className={styles.field}>
      <span id={id} className={styles.label}>
        {field.label}
      </span>
      {value}
      {field.edited && field.detected !== null && canEdit ? (
        <button
          type="button"
          className={styles.reset}
          title={`Tilbage til detekteret: ${field.detected}`}
          aria-label={`${field.label} tilbage til detekteret (${field.detected})`}
          onClick={() => onReset(field.key)}
        >
          <CadIcon name="reset" className={styles.resetIcon} />
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

/**
 * Egenskaber, the floating properties panel (Spacio / Revit), right, only
 * while something is selected: kind and name, provenance chips, short
 * sections of fields. A field edited away from what detection made has an
 * accent border and ↺, which puts that one field back (its tooltip shows
 * the detected value). Footer: Tilbage til detekteret, Slet.
 */
export function PropertiesPanel({ view, more, onField, onReset, onRevert, onDelete, onSelect, onZoom, zone, zoneActions, ceiling, ceilingActions, holes, extra }: PropertiesPanelProps) {
  const canEdit = useCanEdit();
  return (
    <section className={styles.panel} aria-labelledby="geo-props-title">
      <div className={styles.head}>
        <p className={styles.eyebrow}>{view.eyebrow}</p>
        {onZoom && (
          <button type="button" className={styles.zoom} onClick={onZoom}>
            Zoom til
          </button>
        )}
      </div>
      <h2 id="geo-props-title" className={styles.title}>
        {view.title}
      </h2>
      {more > 0 && <p className={styles.more}>+ {more} mere valgt</p>}
      <ul className={styles.chips} aria-label="Status">
        {view.chips.map((c) => (
          <li key={c.label} className={`${styles.chip} ${styles[c.tone]}`}>
            {c.label}
          </li>
        ))}
      </ul>
      {extra}
      {view.sections.map((s) => (
        <div key={s.title} className={styles.section}>
          <h3 className={styles.sectionTitle}>{s.title}</h3>
          {s.fields.map((f) => (
            <Field key={f.key} field={f} canEdit={canEdit} onField={onField} onReset={onReset} />
          ))}
        </div>
      ))}
      {ceiling && ceilingActions && <CeilingSection view={ceiling} canEdit={canEdit} actions={ceilingActions} />}
      {holes && (holes.count > 0 || canEdit) && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Huller</h3>
          {holes.count === 0 && <p className={styles.more}>Ingen trappehuller eller skakte.</p>}
          <p className={styles.related}>
            {Array.from({ length: holes.count }, (_, i) => (
              <span key={i} className={styles.holeChip}>
                Hul {i + 1}
                {canEdit && (
                  <button type="button" className={styles.link} aria-label={`Fjern hul ${i + 1}`} title="Fjern hullet" onClick={() => holes.onRemove(i)}>
                    ×
                  </button>
                )}
              </span>
            ))}
            {canEdit && (
              <button type="button" className={styles.link} onClick={holes.onDraw}>
                + Tegn hul
              </button>
            )}
          </p>
        </div>
      )}
      {zone && zoneActions && <ZoneSection view={zone} canEdit={canEdit} actions={zoneActions} onSelect={onSelect} />}
      {view.related.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{view.kind === 'opening' ? 'Vært' : 'Åbninger'}</h3>
          <p className={styles.related}>
            {view.related.map((r) => (
              <button key={r.guid} type="button" className={styles.link} onClick={() => onSelect(r.guid)}>
                {r.label}
              </button>
            ))}
          </p>
        </div>
      )}
      {canEdit && (view.revertable || view.deletable) && (
        <div className={styles.footer}>
          {view.revertable && (
            <button type="button" className={styles.button} onClick={onRevert}>
              Tilbage til detekteret
            </button>
          )}
          {view.deletable && (
            <button type="button" className={styles.ghost} onClick={onDelete}>
              Slet
            </button>
          )}
        </div>
      )}
    </section>
  );
}
